import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useCarContext } from '../../../contexts/CarContext';
import { CheckCircleIcon, XCircleIcon, TruckIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import axios from 'axios';
import { auth } from '../../../firebase';

const FirstLoginCarModal = () => {
  const { t } = useTranslation();
  const {
    availableCars,
    showFirstLoginModal,
    setShowFirstLoginModal,
    completeFirstLogin
  } = useCarContext();

  const [selectedCars, setSelectedCars] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [carForms, setCarForms] = useState({});
  const [vinValidation, setVinValidation] = useState({});
  const [civValidation, setCivValidation] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // VIN validation effect
  useEffect(() => {
    if (!showFirstLoginModal) return;

    const validateVin = async (carId, vin) => {
      if (!vin || vin.length !== 17) {
        setVinValidation(prev => ({
          ...prev,
          [carId]: { validated: false, validating: false, error: false }
        }));
        return;
      }

      setVinValidation(prev => ({
        ...prev,
        [carId]: { validated: false, validating: true, error: false }
      }));

      try {
        const idToken = await auth.currentUser?.getIdToken();
        const response = await axios.get(
          `https://api.servicegest.ro/driver/car/check-vin-exists?vin=${vin}`,
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            }
          }
        );

        const vinExists = response.data && response.data.exists === true;
        setVinValidation(prev => ({
          ...prev,
          [carId]: { validated: !vinExists, validating: false, error: vinExists }
        }));
      } catch (error) {
        setVinValidation(prev => ({
          ...prev,
          [carId]: { validated: false, validating: false, error: true }
        }));
      }
    };

    // Validate VINs for all selected cars
    Object.entries(carForms).forEach(([carId, form]) => {
      if (form.vin) {
        validateVin(carId, form.vin);
      }
    });
  }, [carForms, showFirstLoginModal]);

  // CIV validation effect
  useEffect(() => {
    if (!showFirstLoginModal) return;

    const validateCiv = (carId, civ) => {
      if (!civ) {
        setCivValidation(prev => ({
          ...prev,
          [carId]: { validated: false, validating: false, error: false }
        }));
        return;
      }

      const civPattern = /^[A-Z]\d{6}$/;
      const isValid = civPattern.test(civ);

      setCivValidation(prev => ({
        ...prev,
        [carId]: { validated: isValid, validating: false, error: !isValid }
      }));
    };

    // Validate CIVs for all selected cars
    Object.entries(carForms).forEach(([carId, form]) => {
      if (form.civ) {
        validateCiv(carId, form.civ);
      }
    });
  }, [carForms, showFirstLoginModal]);

  if (!showFirstLoginModal) return null;

  const handleCarSelect = (carId) => {
    setSelectedCars(prev => {
      const newSelected = prev.includes(carId)
        ? prev.filter(id => id !== carId)
        : [...prev, carId];

      // Initialize form data for newly selected cars
      if (!prev.includes(carId)) {
        const car = availableCars.find(c => (c.id || c.carId) === carId);
        setCarForms(prevForms => ({
          ...prevForms,
          [carId]: {
            owner: false,
            vin: car?.vin || '',
            civ: '',
            registrationNumber: car?.registrationNumber || ''
          }
        }));
      } else {
        // Remove form data for deselected cars
        setCarForms(prevForms => {
          const newForms = { ...prevForms };
          delete newForms[carId];
          return newForms;
        });
        setVinValidation(prev => {
          const newValidation = { ...prev };
          delete newValidation[carId];
          return newValidation;
        });
        setCivValidation(prev => {
          const newValidation = { ...prev };
          delete newValidation[carId];
          return newValidation;
        });
      }

      return newSelected;
    });
  };

  const handleProceedToDetails = () => {
    if (selectedCars.length === 0) {
      toast.error(t('firstLogin.selectAtLeastOneCar'));
      return;
    }
    setShowDetailsModal(true);
  };

  const handleSkip = async () => {
    // Complete first login flow even when skipping
    if (completeFirstLogin) {
      await completeFirstLogin();
    }
  };

  const handleBackToSelection = () => {
    setShowDetailsModal(false);
  };

  const handleFormChange = (carId, field, value) => {
    setCarForms(prev => ({
      ...prev,
      [carId]: {
        ...prev[carId],
        [field]: value
      }
    }));
  };

  const handleSubmitAll = async () => {
    setIsSubmitting(true);

    try {
      const idToken = await auth.currentUser?.getIdToken();
      const promises = selectedCars.map(async (carId) => {
        const form = carForms[carId];
        const vinValid = vinValidation[carId]?.validated;

        // VIN is always required
        if (!vinValid) {
          throw new Error(t('firstLogin.vinRequiredError', { plate: form.registrationNumber }));
        }

        // Validate owner requirements - both VIN and CIV must be valid
        if (form.owner) {
          const civValid = civValidation[carId]?.validated;

          if (!civValid) {
            throw new Error(t('firstLogin.ownerRequiresVinCivError', { plate: form.registrationNumber }));
          }
        }

        return axios.post(
          'https://api.servicegest.ro/driver/car/add',
          {
            owner: form.owner,
            registrationNumber: form.registrationNumber,
            vin: form.vin, // VIN is always required, no undefined fallback
            civ: form.civ || undefined
          },
          {
            headers: {
              'Authorization': `Bearer ${idToken}`,
              'Content-Type': 'application/json',
            }
          }
        );
      });

      await Promise.all(promises);

      toast.success(t('firstLogin.carsAddedSuccess', { count: selectedCars.length }));

      // Complete first login flow and fetch regular car list
      if (completeFirstLogin) {
        await completeFirstLogin();
      }

    } catch (error) {
      console.error('Error adding cars:', error);
      toast.error(error.message || t('firstLogin.addCarsError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showDetailsModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
          <div className="mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('firstLogin.completeDetailsTitle')}
            </h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
              {t('firstLogin.completeDetailsSubtitle')}
            </p>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {selectedCars.map((carId) => {
              const car = availableCars.find(c => (c.id || c.carId) === carId);
              const form = carForms[carId] || {};
              const vinVal = vinValidation[carId] || {};
              const civVal = civValidation[carId] || {};

              return (
                <div key={carId} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 sm:p-4 bg-gray-50 dark:bg-gray-700">
                  <div className="flex items-start sm:items-center mb-3 sm:mb-4">
                    <TruckIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2 mt-0.5 sm:mt-0 flex-shrink-0" />
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                      {car?.make} {car?.type}
                      <span className="block sm:inline sm:ml-2 text-sm sm:text-base font-normal text-gray-600 dark:text-gray-300">
                        {car?.registrationNumber}
                      </span>
                    </h3>
                  </div>

                  <div className="space-y-4 sm:grid sm:grid-cols-1 md:grid-cols-2 sm:gap-4 sm:space-y-0">
                    {/* Owner Checkbox */}
                    <div className="sm:md:col-span-2">
                      <div className="flex items-start sm:items-center">
                        <input
                          type="checkbox"
                          id={`owner-${carId}`}
                          checked={form.owner || false}
                          onChange={(e) => handleFormChange(carId, 'owner', e.target.checked)}
                          className="h-4 w-4 mt-0.5 sm:mt-0 text-purple-600 focus:ring-purple-500 border-gray-300 rounded flex-shrink-0"
                        />
                        <label htmlFor={`owner-${carId}`} className="ml-2 block text-sm text-gray-700 dark:text-slate-300 leading-relaxed">
                          {t('firstLogin.ownerCheckboxLabel')}
                        </label>
                      </div>
                    </div>

                    {/* VIN Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        {t('addVehicle.vin')} <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={form.vin || ''}
                          onChange={(e) => handleFormChange(carId, 'vin', e.target.value)}
                          maxLength={17}
                          placeholder={t('firstLogin.vinPlaceholder')}
                          required
                          className={`w-full px-3 py-2.5 sm:py-2 pr-10 border text-sm ${vinVal.validated ? 'border-green-500' : vinVal.error ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                            } rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-600 dark:text-white`}
                        />
                        {vinVal.validated && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          </div>
                        )}
                        {vinVal.error && !vinVal.validating && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                          </div>
                        )}
                        {vinVal.validating && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                          </div>
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        {t('firstLogin.vinRequiredNote')}
                      </p>
                    </div>

                    {/* CIV Field */}
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${form.owner && !civVal.validated ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-slate-300'}`}>
                        CIV {form.owner && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={form.civ || ''}
                          onChange={(e) => handleFormChange(carId, 'civ', e.target.value)}
                          maxLength={7}
                          placeholder="R012345"
                          className={`w-full px-3 py-2.5 sm:py-2 pr-10 border text-sm ${civVal.validated
                            ? 'border-green-500'
                            : (form.owner && !civVal.validated && form.civ) || civVal.error
                              ? 'border-red-500'
                              : form.owner && !form.civ
                                ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                : 'border-gray-300 dark:border-gray-600'
                            } rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-600 dark:text-white`}
                        />
                        {civVal.validated && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          </div>
                        )}
                        {((form.owner && !civVal.validated && form.civ) || civVal.error) && !civVal.validating && (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <XCircleIcon className="h-5 w-5 text-red-500" />
                          </div>
                        )}
                      </div>
                      <p className={`mt-1 text-xs leading-relaxed ${form.owner && !civVal.validated ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {form.owner && !civVal.validated
                          ? t('firstLogin.civRequiredForOwner') || 'CIV este obligatoriu pentru proprietari'
                          : t('firstLogin.civFormatNote')
                        }
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between gap-3 mt-4 sm:mt-6">
            <button
              onClick={handleBackToSelection}
              className="px-4 sm:px-6 py-2.5 sm:py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors text-sm sm:text-base order-2 sm:order-1"
            >
              ← {t('firstLogin.backToSelection')}
            </button>
            <div className="flex flex-col sm:flex-row gap-3 sm:space-x-3 order-1 sm:order-2">
              <button
                onClick={handleSkip}
                className="px-4 sm:px-6 py-2.5 sm:py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg sm:border-0"
              >
                {t('firstLogin.skipForNow')}
              </button>
              <button
                onClick={handleSubmitAll}
                disabled={isSubmitting || selectedCars.some(carId => {
                  const vinValid = vinValidation[carId]?.validated;
                  const form = carForms[carId] || {};
                  const civValid = civValidation[carId]?.validated;

                  // VIN is always required
                  if (!vinValid) return true;

                  // If owner is checked, CIV must be valid
                  if (form.owner && !civValid) return true;

                  return false;
                })}
                className={`px-4 sm:px-6 py-2.5 sm:py-2 rounded-lg transition-colors text-sm sm:text-base font-medium ${isSubmitting || selectedCars.some(carId => {
                  const vinValid = vinValidation[carId]?.validated;
                  const form = carForms[carId] || {};
                  const civValid = civValidation[carId]?.validated;
                  return !vinValid || (form.owner && !civValid);
                })
                  ? 'bg-gray-400 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'
                  }`}
              >
                {isSubmitting ? t('firstLogin.adding') : t('firstLogin.addSelectedCars', { count: selectedCars.length })}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 max-w-4xl w-full max-h-[95vh] sm:max-h-[80vh] overflow-y-auto">
        <div className="mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('firstLogin.welcomeTitle')}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
            {t('firstLogin.welcomeSubtitle')}
          </p>
        </div>

        <div className="space-y-3 sm:grid sm:grid-cols-1 md:grid-cols-2 sm:gap-4 sm:space-y-0 mb-4 sm:mb-6">
          {availableCars.map((car, index) => {
            const carKey = car.id || car.carId || index;
            const isSelected = selectedCars.includes(carKey);

            return (
              <div
                key={carKey}
                className={`border rounded-lg p-3 sm:p-4 cursor-pointer transition-all ${isSelected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
                  }`}
                onClick={() => handleCarSelect(carKey)}
              >
                <div className="flex items-start sm:items-center justify-between mb-3">
                  <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white leading-tight pr-2">
                    {car.make} {car.type}
                  </h3>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleCarSelect(carKey)}
                    className="w-5 h-5 mt-0.5 sm:mt-0 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 flex-shrink-0"
                  />
                </div>

                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <div className="flex justify-between items-start">
                    <span className="flex-shrink-0">{t('vehicles.plateNumberLabel')}:</span>
                    <span className="font-medium text-gray-900 dark:text-white text-right">
                      {car.registrationNumber || '-'}
                    </span>
                  </div>

                  <div className="flex justify-between items-start">
                    <span className="flex-shrink-0">{t('vehicles.vinLabel')}:</span>
                    <span className="font-medium text-xs text-gray-900 dark:text-white text-right break-all">
                      {car.vin || '-'}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {availableCars.length === 0 && (
          <div className="text-center py-6 sm:py-8">
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
              {t('firstLogin.noVehiclesFound')}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
          <button
            onClick={handleSkip}
            className="px-4 sm:px-6 py-2.5 sm:py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg sm:border-0 order-2 sm:order-1"
          >
            {t('firstLogin.skipForNow')}
          </button>
          <button
            onClick={handleProceedToDetails}
            disabled={selectedCars.length === 0}
            className={`px-4 sm:px-6 py-2.5 sm:py-2 rounded-lg transition-colors text-sm sm:text-base font-medium order-1 sm:order-2 ${selectedCars.length > 0
              ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700'
              : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
          >
            {t('firstLogin.continueWithSelected', { count: selectedCars.length })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginCarModal;